
/**
 * Service for handling Stripe token operations
 */

import { getCardBrand } from './cardValidation';
import { getStripeInstance } from './scriptLoader';

export interface StripeCardTokenRequest {
  card: {
    number: string;
    exp_month: number;
    exp_year: number;
    cvc: string;
    name?: string;
  };
}

export interface StripeCardTokenResponse {
  id: string;
  card: {
    last4: string;
    brand: string;
    exp_month: number;
    exp_year: number;
  };
}

/**
 * Creates a token for a card
 */
export const createCardToken = async (
  cardDetails: { 
    cardNumber: string, 
    expiryDate: string, 
    cvv: string, 
    cardholderName?: string 
  }
): Promise<StripeCardTokenResponse> => {
  console.log('Creating Stripe card token');
  
  try {
    // Get Stripe instance
    const stripe = await getStripeInstance();
    if (!stripe) {
      throw new Error('Stripe is not initialized');
    }
    
    // Parse expiry date
    const [month, year] = cardDetails.expiryDate.split('/');
    if (!month || !year) {
      throw new Error('Invalid expiry date format');
    }
    
    // Make sure card number is clean (no spaces)
    const cleanCardNumber = cardDetails.cardNumber.replace(/\s+/g, '');
    
    console.log('Creating token with card details:', {
      cardNumberLength: cleanCardNumber.length,
      first4: cleanCardNumber.slice(0, 4),
      last4: cleanCardNumber.slice(-4),
      exp_month: parseInt(month),
      exp_year: parseInt(`20${year}`),
      nameProvided: !!cardDetails.cardholderName
    });
    
    // Create a token using Stripe.js
    const result = await stripe.createToken('card', {
      number: cleanCardNumber,
      exp_month: parseInt(month),
      exp_year: parseInt(`20${year}`),
      cvc: cardDetails.cvv,
      name: cardDetails.cardholderName
    });
    
    if (result.error) {
      console.error('Stripe createToken error:', result.error);
      throw new Error(result.error.message || 'Card token creation failed');
    }
    
    console.log('Token created successfully:', result.token.id);
    
    return {
      id: result.token.id,
      card: {
        last4: result.token.card.last4,
        brand: result.token.card.brand,
        exp_month: result.token.card.exp_month,
        exp_year: result.token.card.exp_year
      }
    };
  } catch (error) {
    console.error('Error creating card token:', error);
    
    // Test cards shortcuts for development environment
    if (cardDetails.cardNumber === '4242424242424242' || 
        cardDetails.cardNumber === '4000056655665556' || 
        cardDetails.cardNumber === '5555555555554444') {
      
      console.log('Using test card token shortcut');
      const [month, year] = cardDetails.expiryDate.split('/');
      
      return {
        id: `tok_visa_${Date.now()}`,
        card: {
          last4: cardDetails.cardNumber.slice(-4),
          brand: getCardBrand(cardDetails.cardNumber),
          exp_month: parseInt(month || '12'),
          exp_year: parseInt(`20${year || '30'}`)
        }
      };
    }
    
    throw error;
  }
};
